// BARCINO — Cart manager
// Global (hors Barba) — initialisé dans $(document).ready
// Gère les clics sur [data-cart-add], le POST AJAX, le remplissage de la modal et le compteur nav

const barcinoCart = {

    _toast: null,
    _closeTimer: null,

    init() {
        this._toast = document.getElementById('bc-cart-toast');
        if (!this._toast) return;

        // Ajout panier via événement custom (ex: quick-add modal)
        document.addEventListener('bc:cart:add', (e) => {
            const { variantId, quantity } = e.detail || {};
            if (variantId) this.add(variantId, quantity || 1);
        });

        // Délégation d'événement sur le document pour gérer le panier sur toutes les pages
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-cart-add]');
            if (btn) {
                e.preventDefault();
                e.stopPropagation();
                this._handleAdd(btn);
                return;
            }

            if (e.target.closest('[data-cart-toast-close]')) {
                this._hide();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this._toast.classList.contains('is-visible')) this._hide();
        });

        // Initialise le compteur panier au chargement
        this._refreshCount();
    },

    async _handleAdd(btn) {
        const itemId    = btn.dataset.cartAdd;
        const isBox     = btn.dataset.cartType === 'box';
        const quantity  = parseInt(btn.dataset.cartQty || '1', 10);
        const lang      = document.documentElement.lang || 'fr';
        const endpoint  = `/${lang}/panier/ajouter`;

        if (!itemId) return;

        // Feedback visuel sur le bouton
        btn.classList.add('is-loading');
        btn.setAttribute('disabled', 'true');

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(isBox ? { box_id: itemId, quantity } : { variant_id: itemId, quantity }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Erreur ajout panier');

            this._fillToast(data.item);
            this._show();
            this._updateCount(data.cart_count);

        } catch (err) {
            console.error('[barcinoCart] Erreur ajout :', err);
        } finally {
            btn.classList.remove('is-loading');
            btn.removeAttribute('disabled');
        }
    },

    _fillToast(item) {
        const t = this._toast;
        if (!t || !item) return;

        const img = t.querySelector('.bc-cart-toast__img');
        if (img) {
            img.src = item.image_url || '';
            img.alt = item.product_name || '';
        }

        const set = (sel, val) => {
            const el = t.querySelector(sel);
            if (el) el.textContent = val;
        };

        const quantity = item.quantity || 1;
        const sub = [item.roaster_name, item.grind_type, this._formatWeight(item.weight_grams)]
            .filter(Boolean)
            .join(' • ');

        set('[data-cart-name]',  item.product_name || '');
        set('[data-cart-sub]',   sub);
        set('[data-cart-sku]',   item.sku || '—');
        set('[data-cart-qty]',   quantity);
        set('[data-cart-price]', this._formatPrice(item.unit_price));
        set('[data-cart-total]', this._formatPrice((item.unit_price || 0) * quantity));
    },

    _formatWeight(grams) {
        const g = parseInt(grams || 0, 10);
        if (!g) return '';
        if (g < 1000) return `${g} g`;
        const kg = (g / 1000).toString();
        return `${document.documentElement.lang === 'en' ? kg : kg.replace('.', ',')} kg`;
    },

    _formatPrice(price) {
        if (price == null) return '—';
        const locale = document.documentElement.lang === 'en' ? 'en-GB' : 'fr-FR';
        return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(price);
    },

    _show() {
        const t = this._toast;
        if (!t) return;

        clearTimeout(this._closeTimer);
        t.removeAttribute('hidden');
        // Force le reflow pour que la transition d'ouverture se joue
        void t.offsetWidth;
        t.classList.add('is-visible');

        // Ferme automatiquement après 6s
        this._closeTimer = setTimeout(() => this._hide(), 6000);
    },

    _hide() {
        const t = this._toast;
        if (!t) return;

        clearTimeout(this._closeTimer);

        const modal = t.querySelector('.bc-cart-overlay__modal');
        const onEnd = () => {
            t.setAttribute('hidden', '');
            modal?.removeEventListener('transitionend', onEnd);
        };
        modal?.addEventListener('transitionend', onEnd);

        t.classList.remove('is-visible');
    },

    _updateCount(count) {
        const badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(b => {
            b.textContent = count > 0 ? count : '';
            b.classList.toggle('is-empty', count === 0);
        });
    },

    // Méthode publique pour ajout direct sans bouton DOM.
    // { silent: true } ajoute au panier sans afficher le toast de confirmation
    // (ex : rebuy depuis le suivi de commande, suivi d'une redirection immédiate).
    async add(variantId, quantity = 1, { silent = false } = {}) {
        const lang     = document.documentElement.lang || 'fr';
        const endpoint = `/${lang}/panier/ajouter`;

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ variant_id: variantId, quantity }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Erreur ajout panier');
            if (!silent) {
                this._fillToast(data.item);
                this._show();
            }
            this._updateCount(data.cart_count);
            this._refreshCount();
        } catch (err) {
            console.error('[barcinoCart] Erreur ajout :', err);
        }
    },

    async _refreshCount() {
        try {
            const lang = document.documentElement.lang || 'fr';
            const res  = await fetch(`/${lang}/panier/compter`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok) return;
            const data = await res.json();
            this._updateCount(data.cart_count || 0);
        } catch { /* silencieux */ }
    },
};

export default barcinoCart;
